import { useCallback, useEffect, useRef } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { pluralize } from "@common/helpers/string";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import { useLineItemsBulkAccountCommandOptions } from "@hooks/useLineItemsBulkAccountCommandOptions";

interface UseLineItemsBulkCommandActionsParams {
    selectedCount: number;
    hasSelectedByCurrentFilters: boolean;
    isUpdating: boolean;
    hasReceivablesAccount: boolean;
    hasPayablesAccount: boolean;
    /** Gates the "Set class" bulk action on the CLASSES plan feature. */
    hasClassesAccess: boolean;
    /**
     * Gates the "Accrual ledger", "Receivables account" and "Payables
     * account" bulk actions on the ACCRUAL_LEDGER plan feature — these are
     * all accrual-basis actions and shouldn't be available on plans that
     * don't include accrual ledger access (e.g. Basic/free).
     */
    hasAccrualLedgerAccess: boolean;
    accrualAccountEntityId?: number;
    onClassesSelected?: (classIds: string[]) => void | Promise<void>;
    onAccrualAccountSelected?: (account: Account | null) => void;
    onAccountsReceivableSelected?: (account: Account | null) => void;
    onAccountsPayableSelected?: (account: Account | null) => void;
    onEditClick?: () => void;
    onCreateTaskClick?: () => void;
}

export function useLineItemsBulkCommandActions({
    selectedCount,
    hasSelectedByCurrentFilters,
    isUpdating,
    hasReceivablesAccount,
    hasPayablesAccount,
    hasClassesAccess,
    hasAccrualLedgerAccess,
    accrualAccountEntityId,
    onClassesSelected,
    onAccrualAccountSelected,
    onAccountsReceivableSelected,
    onAccountsPayableSelected,
    onEditClick,
    onCreateTaskClick,
}: UseLineItemsBulkCommandActionsParams) {
    const {
        open: openCommands,
        close,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    const handleApplyClasses = useCallback(
        (classIds: string[]) => {
            close();
            void onClassesSelected?.(classIds);
        },
        [close, onClassesSelected],
    );

    const handleEditAction = useCallback(() => {
        close();
        onEditClick?.();
    }, [close, onEditClick]);

    const handleCreateTaskAction = useCallback(() => {
        close();
        onCreateTaskClick?.();
    }, [close, onCreateTaskClick]);

    const canUseAccrualAccountActions =
        hasAccrualLedgerAccess && !isUpdating && !!accrualAccountEntityId;

    const { accrualCoaOptions, receivablesOptions, payablesOptions } =
        useLineItemsBulkAccountCommandOptions({
            entityId: canUseAccrualAccountActions
                ? accrualAccountEntityId
                : undefined,
            onAccrualAccountSelected: canUseAccrualAccountActions
                ? onAccrualAccountSelected
                : undefined,
            onAccountsReceivableSelected:
                canUseAccrualAccountActions && hasReceivablesAccount
                    ? onAccountsReceivableSelected
                    : undefined,
            onAccountsPayableSelected:
                canUseAccrualAccountActions && hasPayablesAccount
                    ? onAccountsPayableSelected
                    : undefined,
        });

    const selectionLabel = hasSelectedByCurrentFilters
        ? "All matching line items selected"
        : `${selectedCount} ${pluralize("line item", selectedCount)} selected`;

    const canSetClass = hasClassesAccess && !isUpdating;

    const handleOpenBulkActions = useCallback(() => {
        openCommands(CommandsDirectory.LINE_ITEMS_BULK, {
            lineItemsApplyClasses: canSetClass ? handleApplyClasses : undefined,
            lineItemsBulkAccrualCoaOptions:
                accrualCoaOptions.length > 0 ? accrualCoaOptions : undefined,
            lineItemsBulkReceivablesOptions:
                receivablesOptions.length > 0 ? receivablesOptions : undefined,
            lineItemsBulkPayablesOptions:
                payablesOptions.length > 0 ? payablesOptions : undefined,
            onLineItemsBulkEdit:
                onEditClick && !hasSelectedByCurrentFilters && !isUpdating
                    ? handleEditAction
                    : undefined,
            onLineItemsBulkCreateTask: onCreateTaskClick
                ? handleCreateTaskAction
                : undefined,
            lineItemsSelectionLabel: selectionLabel,
            selectedLineItemCount: selectedCount,
            hasSelectedAllMatchingLineItems: hasSelectedByCurrentFilters,
        });
    }, [
        openCommands,
        isUpdating,
        canSetClass,
        handleApplyClasses,
        handleEditAction,
        handleCreateTaskAction,
        onCreateTaskClick,
        accrualCoaOptions,
        receivablesOptions,
        payablesOptions,
        onEditClick,
        hasSelectedByCurrentFilters,
        selectionLabel,
        selectedCount,
    ]);

    const handleOpenRef = useRef(handleOpenBulkActions);
    handleOpenRef.current = handleOpenBulkActions;

    const hasSelection = selectedCount > 0 || hasSelectedByCurrentFilters;

    useEffect(() => {
        if (hasSelection) {
            registerOpenOverride(() => handleOpenRef.current());
            return () => unregisterOpenOverride();
        }
    }, [hasSelection, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        hasSelection,
    };
}
